import { atom, createStore, useAtomValue } from 'jotai';
import { useCallback } from 'react';
import {
  IAppConfigDataModel,
  IGenericSortable,
  sortGenericSortable,
} from '../../data-models/app-config.data-model';
import { ICurrencyDataModel } from '../../data-models/currency.data-model';
import { IFormatterDataModel } from '@foresightdata/web-common-core';
import { IFundDataModel } from '../../data-models/fund.data-model';
import {
  KpiResolutionStrategyEntry,
  KpiSourceCatalogEntry,
  sanitizeKpiResolutionStrategies,
  sanitizeKpiSourceCatalog,
} from '../../data-models/kpi-source-hierarchy.data-model';
import { IMetricsConfigDataModel } from '../../data-models/metrics-config.data-model';
import { IRoundDataModel } from '../../data-models/round.data-model';
import { TransactionCategory, TransactionType } from '../../data-models/transaction-type.data-model';
import { IUserDataModel } from '../../data-models/user.data-model';
import {
  AppClientConfig,
  appClientConfigSchema,
  ClientConfigKey,
  FundWaterfallType,
  parseClientConfig,
} from '../../schemas/ClientConfig.schema';
import { getForesightStore } from '../../util/jotai-store';
import { FundDataModel } from '../../schemas/Fund.schema';
import { CurrencyCode } from '../../util/currency-util';
import { fetchAppConfig } from '../queries/MaggieConfigQueries';

export const defaultCurrency: CurrencyCode = 'USD';

export const clientConfigAtom = atom<AppClientConfig | undefined>();
export const currenciesAtom = atom<ICurrencyDataModel[]>([]);
export const customFormattersAtom = atom<IFormatterDataModel<unknown>[]>([]);
export const fundsAtom = atom<FundDataModel[]>([]);
export const kpiSourceCatalogAtom = atom<KpiSourceCatalogEntry[]>([]);
export const kpiResolutionStrategiesAtom = atom<KpiResolutionStrategyEntry[]>([]);
export const roundsAtom = atom<IRoundDataModel[]>([]);
export const metricsConfigAtom = atom<IMetricsConfigDataModel>({} as IMetricsConfigDataModel);
export const sectorsAtom = atom<IGenericSortable[]>([]);
export const teamsAtom = atom<IGenericSortable[]>([]);
export const transactionTypesAtom = atom<TransactionType[]>([]);
export const usersAtom = atom<IUserDataModel[]>([]);

export async function initConfigs(store: ReturnType<typeof createStore>) {
  const config = await fetchAppConfig();
  initConfigsWithMeta(store, config);
}

export function initConfigsWithMeta(store: ReturnType<typeof createStore>, config: IAppConfigDataModel) {
  store.set(clientConfigAtom, appClientConfigSchema().cast(config.clientConfig));
  store.set(currenciesAtom, config.currencies);
  store.set(customFormattersAtom, config.formatters);
  store.set(fundsAtom, config.funds.sort(sortGenericSortable));
  store.set(kpiSourceCatalogAtom, sanitizeKpiSourceCatalog(config.kpiSources));
  store.set(kpiResolutionStrategiesAtom, sanitizeKpiResolutionStrategies(config.kpiResolutionStrategies));
  store.set(roundsAtom, config.rounds.sort(sortGenericSortable));
  store.set(metricsConfigAtom, config.metricsConfig);
  store.set(sectorsAtom, config.sectors.sort(sortGenericSortable));
  store.set(teamsAtom, config.teams.sort(sortGenericSortable));
  store.set(transactionTypesAtom, config.transactionTypes);
  store.set(
    usersAtom,
    config.users.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
  );
}

export const fundsByIdMapAtom = atom((get) => {
  return get(fundsAtom).reduce((res, fund) => {
    return res.set(fund.id, fund);
  }, new Map<number, IFundDataModel>());
});

// Vehicles (blockers / holdcos) and fund-family shadow funds are funds on the ledger but not reporting funds.
export const isReportingFund = (fund: IFundDataModel): boolean =>
  fund.fundKind !== 'intermediary' && fund.fundKind !== 'family';

// Pickers read this.
export const selectableFundsAtom = atom((get) => get(fundsAtom).filter(isReportingFund));

/**
 * Every strategy on the given funds, sorted, deduped exactly: the strategy select compares with
 * `===`, so `Growth` and `growth` must both stay options or a fund holding one of them shows an
 * unchecked row for the other.
 */
export function collectStrategyOptions(funds: { strategy?: FundDataModel['strategy'] }[]): string[] {
  const strategies = new Set(
    funds.flatMap((fund) => fund.strategy ?? []).filter((strategy): strategy is string => !!strategy)
  );
  return [...strategies].sort((a, b) => a.localeCompare(b));
}

// Strategy pickers (fund config, Fund Profile Summary) offer what the client already uses on any fund.
export const fundStrategiesAtom = atom((get) => collectStrategyOptions(get(fundsAtom)));

export const roundsByIdMapAtom = atom((get) => {
  return get(roundsAtom).reduce((res, round) => {
    return res.set(round.id, round);
  }, new Map<number, IRoundDataModel>());
});

export const roundsByNameMapAtom = atom((get) => {
  return get(roundsAtom).reduce((res, round) => {
    return res.set(round.displayName, round);
  }, new Map<string, IRoundDataModel>());
});

export const sectorsByIdMapAtom = atom((get) => {
  return get(sectorsAtom).reduce((res, sector) => {
    return res.set(sector.id, sector);
  }, new Map<number, IGenericSortable>());
});

export const teamsMapAtom = atom((get) => {
  return get(teamsAtom).reduce((res, team) => {
    return res.set(team.id, team);
  }, new Map<number, IGenericSortable>());
});

export const transactionTypesAndSubTypeAtom = atom((get) => {
  return get(transactionTypesAtom).reduce((res, transactionType) => {
    if (!res.has(transactionType.catergory)) {
      res.set(transactionType.catergory, new Map());
    }
    res.get(transactionType.catergory)!.set(transactionType.name, transactionType);

    return res;
  }, new Map<string, Map<string, TransactionType>>());
});

export const transactionTypesByIdMapAtom = atom((get) => {
  return get(transactionTypesAtom).reduce((res, transactionType) => {
    return res.set(transactionType.id, transactionType);
  }, new Map<number, TransactionType>());
});

export const activeUsersAtom = atom((get) => {
  const allUsers = get(usersAtom);

  return allUsers.filter((user) => user.activeStatus === true);
});

export const activeUserOptionsAtom = atom((get) => {
  const activeUsers = get(activeUsersAtom);

  return activeUsers.map((user) => ({
    id: user.id,
    value: user.name,
    label: user.name,
  }));
});

export const allUserOptionsStateAtom = atom((get) => {
  const allUsers = get(usersAtom);

  return allUsers.map((user) => ({
    id: user.id,
    value: user.name,
    label: user.name,
  }));
});

export const usersByIdMapAtom = atom((get) => {
  const map = new Map<number, IUserDataModel>();
  get(usersAtom).forEach((user) => {
    map.set(user.id, user);
  });

  return map;
});

export const usersByEmailMapAtom = atom((get) => {
  const map = new Map<string, IUserDataModel>();
  get(usersAtom).forEach((user) => {
    map.set(user.email, user);
  });

  return map;
});

export const currencyMapByIdAtom = atom((get) => {
  const currencies = get(currenciesAtom);

  return new Map(currencies.map((c) => [c.id, c]));
});

export const currencyMapByCodeAtom = atom((get) => {
  const currencies = get(currenciesAtom);

  return new Map(currencies.map((c) => [c.code, c]));
});

export function useGetTransactionCategoryAndSubtype() {
  const transactionTypesById = useAtomValue(transactionTypesByIdMapAtom);

  return useCallback(
    (transactionTypeId: number) => {
      const transactionType = transactionTypesById.get(transactionTypeId);
      const transactionCategory = (transactionType?.catergory as TransactionCategory) ?? null;
      const transactionSubType = transactionType?.name ?? null;

      return {
        transactionCategory,
        transactionSubType,
      };
    },
    [transactionTypesById]
  );
}

export const clientConfigFieldsAtom = atom((get) => {
  const clientConfig = get(clientConfigAtom);

  return parseClientConfig(clientConfig);
});

export function getFundWaterfallType(): FundWaterfallType {
  const clientConfig = getForesightStore().get(clientConfigFieldsAtom);
  return (
    (clientConfig?.[ClientConfigKey.FundWaterfallType] as FundWaterfallType) ?? FundWaterfallType.FormBased
  );
}
